import type { FC, ReactElement } from "react"

import memoizeOne, { type MemoizedFn } from "memoize-one"

import { ETypographyAlignment, ETypographySize } from "@/3514/components"
import { type TScreenBreakpoints, useScreenBreakpoints } from "@/3514/hooks"
import { type IUseTranslation, useTranslation } from "@/hooks"

import {
    type TDiagnosticSummarySuspenseQuery as TSuspenseQuery,
    useDiagnosticSummarySuspenseQuery as useSuspenseQuery
} from "./diagnostic-summary.api.queries.hook"
import type {
    TDiagnosticSummaryLikertMapper as TLikertMapper,
    TDiagnosticSummaryEntityProps as TProps,
    TDiagnosticSummaryFreeResponsesMapper as TResponsesMapper
} from "./diagnostic-summary.ui.types"
import uiUtils from "./diagnostic-summary.ui.utils"
import View from "./diagnostic-summary.view"

const memoizedResponsesMapper: MemoizedFn<TResponsesMapper> = memoizeOne(uiUtils.freeResponsesMapper)
const memoizedLikertMapper: MemoizedFn<TLikertMapper> = memoizeOne(uiUtils.likertQuestionsMapper)

const DiagnosticSummaryParticipantContainer: FC<TProps> = ({
    participantId,
    wrapperClassName = String("")
}: TProps): ReactElement => {
    const { data: diagnosticData }: TSuspenseQuery = useSuspenseQuery({ participantId })

    const { t }: IUseTranslation = useTranslation()

    const breakpoints: TScreenBreakpoints = useScreenBreakpoints()

    const entityYGap: number = breakpoints.xs || breakpoints.sm ? 20 : 50

    return (
        <View
            data={diagnosticData}
            titleText={t("entities.diagnosticSummary.participant.title")}
            titleAlignment={ETypographyAlignment.Center}
            titleSize={ETypographySize.Heading1}
            freeResponsesMapper={memoizedResponsesMapper}
            likertQuestionsMapper={memoizedLikertMapper}
            entityYGap={entityYGap}
            wrapperClassName={wrapperClassName}
            withAnimation
        />
    )
}

DiagnosticSummaryParticipantContainer.displayName = "DiagnosticSummaryParticipantContainer"

export default DiagnosticSummaryParticipantContainer
